import { useEffect, useRef, useState } from 'react';
import { api } from '@/api/client';
import { getSession } from '@/auth/session';
import type { RumLive } from '@/api/types';

const BASE = '/api/v1/controlplane';

/** Backoff between reconnects, in ms. Grows, then holds — it never gives up. */
const RETRY_STEPS = [1000, 2000, 5000, 10_000, 30_000];

export interface RumLiveState {
  data?: RumLive;
  /** True while a stream is open and delivering. Drives the "LIVE" indicator. */
  connected: boolean;
  error?: string;
}

/**
 * Streams the live snapshot over Server-Sent Events.
 *
 * <b>Not `EventSource`.</b> The browser's EventSource cannot send an `Authorization` header, and
 * this endpoint requires a bearer token; the usual workaround is a token in the query string, which
 * writes the credential into every proxy and server access log. So the stream is read from `fetch`
 * with a proper header and parsed by hand — that is the entire reason this hook is not four lines.
 *
 * Falls back to polling if streaming fails outright, because a dashboard that shows nothing is worse
 * than one that updates every few seconds.
 */
export function useRumLive(siteKey: string, pagePath?: string, enabled = true): RumLiveState {
  const [state, setState] = useState<RumLiveState>({ connected: false });

  // Held in a ref so the effect can cancel in-flight work without being re-created on every render.
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    let attempt = 0;
    let pollTimer: ReturnType<typeof setTimeout> | undefined;

    const params = new URLSearchParams({ site_key: siteKey });
    if (pagePath) params.set('page_path', pagePath);

    /** One-shot read, used until streaming is confirmed working and whenever it fails. */
    const pollOnce = async () => {
      try {
        const data = await api.rumLive(siteKey, pagePath);
        if (!cancelled) setState((prev) => ({ ...prev, data, error: undefined }));
      } catch (error) {
        if (!cancelled) {
          setState((prev) => ({
            ...prev,
            error: error instanceof Error ? error.message : 'Live data unavailable',
          }));
        }
      }
    };

    const startPolling = () => {
      const tick = async () => {
        if (cancelled) return;
        await pollOnce();
        if (!cancelled) pollTimer = setTimeout(tick, 5000);
      };
      void tick();
    };

    const connect = async () => {
      const session = getSession();
      if (!session) return;

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const response = await fetch(`${BASE}/rum/stream?${params}`, {
          headers: { Authorization: `Bearer ${session.accessToken}`, Accept: 'text/event-stream' },
          signal: controller.signal,
          cache: 'no-store',
        });

        if (!response.ok || !response.body) throw new Error(`stream failed (${response.status})`);

        attempt = 0;
        if (!cancelled) setState((prev) => ({ ...prev, connected: true, error: undefined }));

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (!cancelled) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // SSE frames are separated by a blank line. A frame can arrive split across reads, so the
          // tail stays in the buffer until its terminator shows up — parsing per-read would produce
          // truncated JSON under exactly the conditions that matter (a busy stream).
          const frames = buffer.split('\n\n');
          buffer = frames.pop() ?? '';

          for (const frame of frames) {
            const line = frame.split('\n').find((l) => l.startsWith('data:'));
            if (!line) continue;
            try {
              const data = JSON.parse(line.slice(5).trim()) as RumLive;
              if (!cancelled) setState({ data, connected: true });
            } catch {
              /* a partial or malformed frame is skipped; the next tick supersedes it anyway */
            }
          }
        }

        throw new Error('stream ended');
      } catch (error) {
        if (cancelled || controller.signal.aborted) return;

        setState((prev) => ({ ...prev, connected: false }));

        // First failure: start polling immediately so the page keeps showing numbers while the
        // stream is retried in the background.
        if (attempt === 0 && !pollTimer) startPolling();

        const delay = RETRY_STEPS[Math.min(attempt, RETRY_STEPS.length - 1)];
        attempt += 1;
        setTimeout(() => { if (!cancelled) void connect(); }, delay);
      }
    };

    // Paint something before the first frame arrives — the stream's first tick is up to a second
    // away, and an empty dashboard in that gap reads as "no traffic".
    void pollOnce();
    void connect();

    return () => {
      cancelled = true;
      abortRef.current?.abort();
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [siteKey, pagePath, enabled]);

  return state;
}
